import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { PreInterviewQuestionsRequest } from '../pre-interview-questions-typings';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { PreInterviewQuestionsApiService } from '../pre-interview-questions-services/pre-interview-questions-api.service';
import { FeatureService, TalentAppFeatures } from "../../service/feature.service";
import { PreInterviewQuestion } from "../../../typings";

@Component({
    selector: 'app-pre-interview-questions-edit',
    templateUrl: './pre-interview-questions-edit.component.html',
    styleUrls: ['./pre-interview-questions-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PreInterviewQuestionsEditComponent implements OnInit, OnChanges {
  @ViewChildren('questionComponentCheckbox') questionComponentCheckboxes!: QueryList<ElementRef>;
  @Input() orderId: string;
  @Input() agentId: number;
  @Input() staticQuestionsList: PreInterviewQuestion[];
  @Input() customQuestionsList: PreInterviewQuestion[];
  @Input() activeQuestionCount = 0;
  @Input() aiQuestionsList: PreInterviewQuestion[] = [];

  aiGeneratedFeature: Boolean;
  // This maps question IDs to whether or not the question is hidden.
  // The edit box is shown when the value is true
  hiddenAiQuestionsMap: Map<number, boolean> = new Map<number, boolean>();


  pageState = 'edit';
  headerList: string[];
  formGroup: UntypedFormGroup;

  constructor(protected formBuilder: UntypedFormBuilder,
              protected preInterviewApi: PreInterviewQuestionsApiService,
              private featureService: FeatureService) {
    this.initializeFormGroup();
    this.aiGeneratedFeature = this.featureService.getFeature(TalentAppFeatures.AI_GENERATED_PRE_INTERVIEW_QUESTIONS);
  }

  ngOnInit(): void {
    window.addEventListener('message', payload => {
      if (payload.data == 'submit') {
        this.submitPreInterviewQuestions();
      }
    });
  }

  ngOnChanges(): void {
    if (this.staticQuestionsList != null && this.customQuestionsList != null) {
      this.getQuestionHeaders();
      this.addEmptyCustomQuestions();
      this.populateFormGroup();
    }
    if (this.aiQuestionsList && this.aiQuestionsList.length > 0) {
      for (let i = 0; i < this.aiQuestionsList.length; i++) {
        this.hiddenAiQuestionsMap.set(i, false);
      }
    }
  }

  initializeFormGroup() {
    this.formGroup = this.formBuilder.group({
      staticQuestions: new UntypedFormGroup({
        staticQuestion36: new UntypedFormControl(false),
        staticQuestion37: new UntypedFormControl(false),
        staticQuestion38: new UntypedFormControl(false),
        staticQuestion39: new UntypedFormControl(false),
        staticQuestion40: new UntypedFormControl(false),
        staticQuestion41: new UntypedFormControl(false),
        staticQuestion42: new UntypedFormControl(false),
        staticQuestion43: new UntypedFormControl(false)
      }),
      customQuestions: new UntypedFormGroup({
        customQuestion1: new UntypedFormControl(false),
        customQuestionText1: new UntypedFormControl(''),
        customQuestion2: new UntypedFormControl(false),
        customQuestionText2: new UntypedFormControl(''),
        customQuestion3: new UntypedFormControl(false),
        customQuestionText3: new UntypedFormControl('')
      }),
      aiQuestions: new UntypedFormGroup({
        aiQuestion1: new UntypedFormControl(false),
        aiQuestionText1: new UntypedFormControl(''),
        aiQuestion2: new UntypedFormControl(false),
        aiQuestionText2: new UntypedFormControl(''),
        aiQuestion3: new UntypedFormControl(false),
        aiQuestionText3: new UntypedFormControl(''),
        aiQuestion4: new UntypedFormControl(false),
        aiQuestionText4: new UntypedFormControl(''),
        aiQuestion5: new UntypedFormControl(false),
        aiQuestionText5: new UntypedFormControl(''),
        aiQuestion6: new UntypedFormControl(false),
        aiQuestionText6: new UntypedFormControl(''),
        aiQuestion7: new UntypedFormControl(false),
        aiQuestionText7: new UntypedFormControl(''),
        aiQuestion8: new UntypedFormControl(false),
        aiQuestionText8: new UntypedFormControl(''),
        aiQuestion9: new UntypedFormControl(false),
        aiQuestionText9: new UntypedFormControl(''),
        aiQuestion10: new UntypedFormControl(false),
        aiQuestionText10: new UntypedFormControl('')
      })
    });
  }

  populateFormGroup(): void {
    const staticQuestions = this.formGroup.get('staticQuestions');
    const customQuestionControls = this.formGroup.get('customQuestions');
    const customQuestionsList = this.customQuestionsList.filter(question => question.isActive);

    Object.keys(staticQuestions['controls']).forEach(key => {
      const questionId = key.substr(key.length - 2);
      const staticQuestion = this.staticQuestionsList.filter(question => question.questionId.toString() === questionId)[0];
      if(staticQuestion.isActive) {
        staticQuestions.get('staticQuestion' + questionId.toString()).patchValue(staticQuestion.selected);
      }
    });

    let i: number = 1;

    for (let customQuestion of customQuestionsList) {
      customQuestionControls.get(`customQuestion${i}`).patchValue(customQuestion.selected);
      customQuestionControls.get(`customQuestionText${i}`).patchValue(customQuestion.description);

      i++;
    }
  }

  getQuestionHeaders(): void {
    const headerList = this.staticQuestionsList.map(question => question.headerKey);
    this.headerList = [...new Set(headerList)];
  }

  addEmptyCustomQuestions(): void {
    const list = this.customQuestionsList.filter(question => question.questionCategory == 'CUSTOM' && question.isActive);

    while (list.length < 3) {
      list.push({ questionCategory: 'CUSTOM', selected: false });
    }

    this.customQuestionsList = list;
  }

  handleCheckboxClickedEvent(question: PreInterviewQuestion, questionIndex: number, event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      if (question.questionCategory === 'CUSTOM') {
        if (!event.target.checked) {
          question.description = null;
          event.target.value = null;
        }
      }
      question.selected = event.target.checked;
      this.countSelectedQuestions();
    }
    if (question.questionCategory === 'AI') {
      this.hiddenAiQuestionsMap[questionIndex] = false;
    }
  }

  countSelectedQuestions(): void {
    const selectedQuestions = this.questionComponentCheckboxes.map(checkbox => checkbox.nativeElement.checked);
    let count = 0;
    selectedQuestions.forEach(selected => {
      if (selected === true) {
        count += 1;
      }
    });
    this.activeQuestionCount = count;
  }

  submitPreInterviewQuestions(): void {
    const selectedQuestions: number[] = [];
    Object.keys(this.formGroup.get('staticQuestions')['controls']).forEach(key => {
      const questionKey = parseInt(key.substr(key.length - 2));

      if (this.formGroup.get('staticQuestions').get(key).value) {
        selectedQuestions.push(questionKey);
      }
    });

    const request: PreInterviewQuestionsRequest = {
      orderId: parseInt(this.orderId),
      agentId: this.agentId,
      selectedQuestionIds: selectedQuestions,
      customQuestions: this.customQuestionsList,
      aiQuestions: this.aiQuestionsList.filter(question => question.selected)
    };
      this.preInterviewApi.savePreInterviewQuestions(request).subscribe();
  }

  filterQuestionsByHeader(header: string): PreInterviewQuestion[] {
    return this.staticQuestionsList.filter(question => question.headerKey === header);
  }

  showEditBox(questionIndex: number): void {
    this.hiddenAiQuestionsMap[questionIndex] = true;
  }

  updateCustomQuestionText(questionIndex: number, newQuestion: string): void {
    let question = this.aiQuestionsList[questionIndex];
    question.description = newQuestion;
    question.questionKey = newQuestion;
    this.aiQuestionsList[questionIndex] = question;
    this.hiddenAiQuestionsMap[questionIndex] = false;
  }
}
